import { useState } from 'react'
import { normalizeUrl } from '../lib/reportUtils'

function buildCsv(detail) {
  const allCategories = Object.keys(detail)
  const allModels = [...new Set(allCategories.flatMap(cat => Object.keys(detail[cat] || {})))]
  const categories = allCategories.filter(cat => allModels.some(m => (detail[cat]?.[m] || 0) > 0))
  const models = allModels.filter(m => categories.some(cat => (detail[cat]?.[m] || 0) > 0))

  const escape = v => `"${String(v).replace(/"/g, '""')}"`
  const rows = []
  rows.push(['Catégorie', ...models, 'Total'].map(escape).join(','))
  for (const cat of categories) {
    const rowTotal = models.reduce((s, m) => s + (detail[cat]?.[m] || 0), 0)
    rows.push([cat, ...models.map(m => detail[cat]?.[m] || 0), rowTotal].map(escape).join(','))
  }
  const colTotals = models.map(m => categories.reduce((s, cat) => s + (detail[cat]?.[m] || 0), 0))
  const grand = colTotals.reduce((s, v) => s + v, 0)
  rows.push(['Total', ...colTotals, grand].map(escape).join(','))

  return rows.join('\n')
}

function aggregateSources(report) {
  const hostData = {}

  for (const llm of (report?.llm_results || [])) {
    for (const [category, catData] of Object.entries(llm.categories || {})) {
      for (const [rawUrl, count] of Object.entries(catData.web_on_urls || {})) {
        const cleanUrl = normalizeUrl(rawUrl)
        let hostname
        try { hostname = new URL(cleanUrl).hostname } catch { hostname = cleanUrl }

        if (!hostData[hostname]) hostData[hostname] = { total: 0, pages: {} }
        hostData[hostname].total += count

        if (!hostData[hostname].pages[cleanUrl]) {
          hostData[hostname].pages[cleanUrl] = { total: 0, detail: {} }
        }
        hostData[hostname].pages[cleanUrl].total += count

        const det = hostData[hostname].pages[cleanUrl].detail
        if (!det[category]) det[category] = {}
        det[category][llm.llm_name] = (det[category][llm.llm_name] || 0) + count
      }
    }
  }

  for (const host of Object.values(hostData)) {
    const merged = {}
    for (const page of Object.values(host.pages)) {
      for (const [cat, models] of Object.entries(page.detail)) {
        if (!merged[cat]) merged[cat] = {}
        for (const [model, count] of Object.entries(models)) {
          merged[cat][model] = (merged[cat][model] || 0) + count
        }
      }
    }
    host.detail = merged
  }

  return Object.entries(hostData).sort(([, a], [, b]) => b.total - a.total)
}

function DetailsTable({ detail, fontSize = 'text-[0.7rem]', padding = 'px-2 py-0.5' }) {
  const allCategories = Object.keys(detail)
  const allModels = [...new Set(allCategories.flatMap(cat => Object.keys(detail[cat] || {})))]

  const categories = allCategories.filter(cat =>
    allModels.some(m => (detail[cat]?.[m] || 0) > 0)
  )
  const models = allModels.filter(m =>
    categories.some(cat => (detail[cat]?.[m] || 0) > 0)
  )

  if (!categories.length || !models.length) return null

  const showTotalCol = models.length >= 2
  const showTotalRow = categories.length >= 2

  const rowTotal = (cat) => models.reduce((s, m) => s + (detail[cat]?.[m] || 0), 0)
  const colTotal = (m) => categories.reduce((s, cat) => s + (detail[cat]?.[m] || 0), 0)
  const grandTotal = categories.reduce((s, cat) => s + rowTotal(cat), 0)

  return (
    <table className={`${fontSize} border-collapse w-full`}>
      <thead>
        <tr className="bg-[#f6f3ee]">
          <th className={`${padding} text-left text-[#77726e] font-medium border-b border-[#77726e]/20`}></th>
          {models.map(m => (
            <th key={m} className={`${padding} text-center text-[#4e6498] font-semibold border-b border-[#77726e]/20 whitespace-nowrap`}>
              {m}
            </th>
          ))}
          {showTotalCol && (
            <th className={`${padding} text-center text-[#2f3126] font-bold border-b border-l border-[#77726e]/30 whitespace-nowrap`}>
              Total
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {categories.map((cat, idx) => (
          <tr key={cat} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#f6f3ee]'}>
            <td className={`${padding} text-left text-[#2f3126] font-medium whitespace-nowrap pr-4`}>
              {cat}
            </td>
            {models.map(m => (
              <td key={m} className={`${padding} text-center text-[#2f3126]`}>
                {(detail[cat]?.[m] || 0) || ''}
              </td>
            ))}
            {showTotalCol && (
              <td className={`${padding} text-center font-bold text-[#2f3126] border-l border-[#77726e]/20`}>
                {rowTotal(cat)}
              </td>
            )}
          </tr>
        ))}
        {showTotalRow && (
          <tr className="border-t border-[#77726e]/30 bg-[#f6f3ee]">
            <td className={`${padding} text-left text-[#2f3126] font-bold`}>Total</td>
            {models.map(m => (
              <td key={m} className={`${padding} text-center font-bold text-[#2f3126]`}>
                {colTotal(m)}
              </td>
            ))}
            {showTotalCol && (
              <td className={`${padding} text-center font-bold text-[#2f3126] border-l border-[#77726e]/20`}>
                {grandTotal}
              </td>
            )}
          </tr>
        )}
      </tbody>
    </table>
  )
}

function DetailsModal({ detail, label, onClose }) {
  const filename = `sources_${label.replace(/[^a-z0-9.-]/gi, '_')}.csv`

  function downloadCsv() {
    const csv = buildCsv(detail)
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col border border-[#77726e]/30"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-3.5 border-b border-[#77726e]/20 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-[#77726e] mb-0.5">Détails des références</p>
            <p className="text-sm font-medium text-[#2f3126] truncate" title={label}>{label}</p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 text-[#77726e] hover:text-[#2f3126] text-lg leading-none transition"
          >
            ✕
          </button>
        </div>
        <div className="overflow-auto p-4 flex-1">
          <DetailsTable detail={detail} fontSize="text-xs" padding="px-3 py-1.5" />
        </div>
        <div className="px-5 py-3 border-t border-[#77726e]/20 flex justify-end">
          <button
            onClick={downloadCsv}
            className="px-3 py-1.5 text-xs font-semibold border border-[#4e6498]/40 bg-[#4e6498]/10 text-[#4e6498] hover:bg-[#4e6498]/20 transition"
          >
            Télécharger CSV
          </button>
        </div>
      </div>
    </div>
  )
}

function DetailsTooltip({ detail }) {
  return (
    <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-[#77726e]/30 shadow-lg p-2 min-w-max pointer-events-none">
      <DetailsTable detail={detail} />
    </div>
  )
}

function CountBadge({ n }) {
  return (
    <span className="shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-[#4e6498]/10 text-[#4e6498] font-bold text-[0.7rem]">
      {n}
    </span>
  )
}

function DetailsTag({ detail, label = '' }) {
  const [hovered, setHovered] = useState(false)
  const [open, setOpen] = useState(false)
  return (
    <span
      className="relative shrink-0"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={e => { e.stopPropagation(); setOpen(true) }}
    >
      <span className="inline-block px-1.5 py-0.5 text-[0.7rem] font-semibold border border-[#77726e]/30 bg-[#f6f3ee] text-[#77726e] cursor-pointer select-none hover:bg-[#edeae4] transition">
        Détails
      </span>
      {hovered && <DetailsTooltip detail={detail} />}
      {open && <DetailsModal detail={detail} label={label} onClose={() => setOpen(false)} />}
    </span>
  )
}

function SubPageRow({ url, total, detail }) {
  const displayUrl = url.length > 60 ? url.slice(0, 60) + '…' : url
  return (
    <div className="flex items-center gap-2 pl-10 pr-3 py-1.5 bg-white border-x border-b border-[#77726e]/20">
      <CountBadge n={total} />
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="truncate flex-1 text-xs text-[#4e6498] hover:underline hover:text-[#2f3126] transition"
        title={url}
      >
        {displayUrl}
      </a>
      <DetailsTag detail={detail} label={url} />
    </div>
  )
}

function HostRow({ hostname, data }) {
  const [open, setOpen] = useState(false)
  const pages = Object.entries(data.pages).sort(([, a], [, b]) => b.total - a.total)

  return (
    <div>
      <div
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-1.5 border border-[#77726e]/25 bg-[#f6f3ee] hover:bg-[#edeae4] cursor-pointer transition select-none"
      >
        <CountBadge n={data.total} />
        <span className="flex-1 text-xs font-medium text-[#2f3126]">{hostname}</span>
        {pages.length > 1 && (
          <span className="text-[#77726e] text-[0.7rem]">{pages.length} pages</span>
        )}
        <DetailsTag detail={data.detail} label={hostname} />
      </div>
      {open && (
        <div className="divide-y divide-[#77726e]/10">
          {pages.map(([url, pageData]) => (
            <SubPageRow key={url} url={url} total={pageData.total} detail={pageData.detail} />
          ))}
        </div>
      )}
    </div>
  )
}

export default function SourcesPanorama({ report }) {
  const entries = aggregateSources(report)

  if (!entries.length) {
    return (
      <div className="text-center py-8 text-sm text-[#77726e]">
        Aucune source disponible.
      </div>
    )
  }

  const totalSites = entries.length
  const totalRefs = entries.reduce((s, [, d]) => s + d.total, 0)

  return (
    <div className="w-full">
      <p className="text-[0.7rem] text-[#77726e] mb-4">
        {totalSites} site{totalSites > 1 ? 's' : ''} · {totalRefs} référence{totalRefs > 1 ? 's' : ''} au total
      </p>
      <div className="space-y-1.5">
        {entries.map(([hostname, data]) => (
          <HostRow key={hostname} hostname={hostname} data={data} />
        ))}
      </div>
    </div>
  )
}
